import * as React from 'react';

import type { StatePlaceholderDensity } from './state-placeholder-variants';

/**
 * Internal wiring context: carries the density every slot must agree on, the caller's
 * test-id namespace, and the title registration used by the dev-only page warning.
 *
 * Presentation plumbing only — no feature data, no copy. `libs/ui` components stay generic
 * and context-free of domain state (see `libs/ui/CLAUDE.md`).
 */
export interface StatePlaceholderContextValue {
  density: StatePlaceholderDensity;
  /**
   * Namespaced per semantic component, so `RestrictedPlaceholder` keeps emitting
   * `restricted-placeholder-title` while `NotFoundPlaceholder` emits its own.
   */
  testId: (element: string) => string;
  /** Called by `Title` on mount so the Root can tell a headingless route from a titled one. */
  registerTitle: () => void;
}

/**
 * The Provider is NOT re-exported under its own name: an exported `Context.Provider` reads as
 * a component to `react-refresh/only-export-components`, which then rejects the hook beside it.
 * `StatePlaceholder` renders `<StatePlaceholderContext.Provider>` directly instead, so this
 * module exports no component and Fast Refresh stays intact.
 */
export const StatePlaceholderContext =
  React.createContext<StatePlaceholderContextValue | null>(null);

StatePlaceholderContext.displayName = 'StatePlaceholderContext';

export const useStatePlaceholderContext = (): StatePlaceholderContextValue => {
  const context = React.useContext(StatePlaceholderContext);

  if (!context) {
    throw new Error(
      'StatePlaceholder.* must be used within <StatePlaceholder>',
    );
  }

  return context;
};
